import { jsx, jsxs } from 'react/jsx-runtime';
import { useResourceDataContextSafe } from '../../../../../resource-detail/components/context/resourceDataContext.js';
import { ColorLabel } from '../../components/ColorLabel.js';
import { containerPreviewCompact } from '../cssPreviewContainer.js';
import { cssAspectRatio } from '../sharedCss.js';
import { isResourceDetailImage, isResourceDetailVideo, isResourceDetailDocument } from '../../../../utils/resourceDetailGuards.js';
import { styled, css } from 'styled-components';
import Color from 'color';
import { IconVideo } from '@fabric-app/icons/old';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { Flex } from '@fabric-app/ui/components/Flex';
import { Image } from '@fabric-app/ui/components/Image';
import { isResourceDetailDocumentPDF, isResourceDetailDocumentJSON, isResourceDetailDocumentHTML } from '../../../../documentGuards.js';
import { decodeHTMLEntities } from '@fabric-app/utils/text';
import { getResourceNamePlaceholder } from '../../../../utils/getResourceTitle.js';
import { TitlePlaceholder } from './ResourcePreviewNotepad.js';
import { ResourcePreviewTitle } from '../../components/Typography.js';
import { ResourceGridViewLoadingIndicator } from '../components/ResourceGridViewLoadingIndicator.js';
import { isString } from '@fabric-app/utils/guards';
import { IconFileName } from '../../components/IconFileName.js';
import Divider from '@fabric-app/ui/components/Divider';
import { HTMLRender } from '@fabric-app/ui/html/components/HTMLRender';
import { CodeRender } from '../../../../../code/components/CodeRender.js';
import { FileIcon } from '@fabric-app/icons/icons';
import { useResourceListPreferencesContextUnsafe } from '../../../ResourceList/contexts/resourceListContextPreferences.js';
import { getLightenedColor } from '@fabric-app/utils/color';
import { useMemo } from 'react';
import { getResourceDimensions } from '../../../../utils/getResourceDimensions.js';
import { IconCard30pxKindPdf } from '@fabric-app/icons/svg';
import { PreviewIconContainer } from '../components/PreviewIconContainer.js';

const ResourcePreviewFile = () => {
  const { resource, placeholderName } = useResourceDataContextSafe();
  const pref = useResourceListPreferencesContextUnsafe();
  const isAutoHeight = pref?.viewMode === "Masonry";
  const backgroundColor = useMemo(() => {
    try {
      if (isResourceDetailImage(resource) && resource.data.dominantColors) {
        return getLightenedColor(new Color(resource.data.dominantColors[0].rgb).hex(), 0.5);
      }
      return void 0;
    } catch {
      return void 0;
    }
  }, [
    resource
  ]);
  if (resource.thumbnail) {
    const dimensions = getResourceDimensions(resource);
    return /* @__PURE__ */ jsx(Wrapper, {
      isAutoHeight,
      children: /* @__PURE__ */ jsxs(ThumbnailWrapper, {
        style: isAutoHeight ? {
          width: "100%"
        } : void 0,
        children: [
          /* @__PURE__ */ jsx(ColorLabel, {
            absPosition: "inside-top-left"
          }),
          /* @__PURE__ */ jsx(ResourceGridViewLoadingIndicator, {
            resource
          }),
          isResourceDetailVideo(resource) && /* @__PURE__ */ jsx(PreviewIconContainer, {
            children: /* @__PURE__ */ jsx(IconVideo, {})
          }),
          isResourceDetailDocumentPDF(resource) && /* @__PURE__ */ jsx(PreviewIconContainer, {
            children: /* @__PURE__ */ jsx(IconCard30pxKindPdf, {})
          }),
          /* @__PURE__ */ jsx(ThumbnailImage, {
            isSvg: resource.extension === "svg",
            "data-preview-content-wrapper": "stored_file_thumbnail",
            /**
            * load higher image quality if image is a vertical noodle
            */
            src: isAutoHeight && !!dimensions && dimensions.width / dimensions.height > 0.5 ? resource.thumbnail.md : resource.thumbnail.lg,
            alt: resource.name || placeholderName,
            style: isAutoHeight && dimensions ? {
              aspectRatio: `${dimensions.width} / ${dimensions.height}`,
              maxHeight: "600px",
              width: "100%",
              objectFit: "cover",
              objectPosition: "top",
              backgroundColor
            } : {
              backgroundColor
            }
          })
        ]
      })
    });
  } else if (isResourceDetailDocument(resource) && isString(resource.data?.documentPreview?.html)) {
    return /* @__PURE__ */ jsxs(Wrapper, {
      "data-preview-content-wrapper": "stored_document",
      children: [
        /* @__PURE__ */ jsx(ColorLabel, {
          absPosition: "inside-top-left"
        }),
        /* @__PURE__ */ jsx(ResourceGridViewLoadingIndicator, {
          resource
        }),
        /* @__PURE__ */ jsxs(Flex, {
          alignItems: "center",
          maxWidth: "100%",
          overflow: "hidden",
          flexShrink: false,
          children: [
            /* @__PURE__ */ jsx(ResourcePreviewTitle, {
              style: {
                flexShrink: 1
              },
              children: resource.name || /* @__PURE__ */ jsx(TitlePlaceholder, {
                children: getResourceNamePlaceholder(resource)
              })
            }),
            /* @__PURE__ */ jsx(ResourcePreviewTitle, {
              children: resource.extension ? `.${resource.extension}` : ""
            })
          ]
        }),
        /* @__PURE__ */ jsx(StyledDivider, {
          borderColor: "tertiary"
        }),
        isResourceDetailDocumentJSON(resource) || isResourceDetailDocumentHTML(resource) ? /* @__PURE__ */ jsx(StyledCodeRender, {
          code: decodeHTMLEntities(resource.data.documentPreview.html),
          mimeType: resource.mimeType ?? "application/json",
          overflow: "hidden",
          truncate: 750
        }) : /* @__PURE__ */ jsx(StyledHTMLRender, {
          html: resource.data.documentPreview.html,
          truncate: 750
        })
      ]
    });
  } else {
    return /* @__PURE__ */ jsxs(Wrapper, {
      "data-preview-content-wrapper": "stored_file_icon",
      children: [
        /* @__PURE__ */ jsx(ColorLabel, {
          absPosition: "inside-top-left"
        }),
        /* @__PURE__ */ jsx(ResourceGridViewLoadingIndicator, {
          resource
        }),
        /* @__PURE__ */ jsx(FileIcon, {
          height: 90,
          width: 120,
          extension: resource.extension || "?"
        }),
        /* @__PURE__ */ jsx(IconFileNameContainer, {
          children: /* @__PURE__ */ jsx(IconFileName, {
            title: resource.name || placeholderName,
            extension: resource.extension
          })
        })
      ]
    });
  }
};
const StyledDivider = styled(Divider).withConfig({
  displayName: "ResourcePreviewStoredFile__StyledDivider",
  componentId: "sc-850f8c32-0"
})([
  ``,
  `{display:none;}`
], containerPreviewCompact);
const StyledCodeRender = styled(CodeRender).withConfig({
  displayName: "ResourcePreviewStoredFile__StyledCodeRender",
  componentId: "sc-850f8c32-1"
})([
  `width:100%;font-size:13px;line-height:1.5;overflow:hidden;&& *{overflow:hidden;}pre code.hljs{white-space:pre-wrap;}opacity:0.6;`
]);
const StyledHTMLRender = styled(HTMLRender).withConfig({
  displayName: "ResourcePreviewStoredFile__StyledHTMLRender",
  componentId: "sc-850f8c32-2"
})([
  `pointer-events:none;flex-grow:1;flex-shrink:1;width:100%;font-size:13px;line-height:1.5;color:`,
  `;overflow:hidden;text-overflow:ellipsis;&& *{overflow:hidden;}h1,h2,h3,h4,h5,h6,a{font-size:1em;color:`,
  `;}div{display:block;margin-block-start:1em;margin-block-end:1em;}`,
  `{*{font-size:0.75rem !important;line-height:1rem !important;}}`
], cssVar["color-text-tertiary"], cssVar["color-text-tertiary"], containerPreviewCompact);
const IconFileNameContainer = styled.div.withConfig({
  displayName: "ResourcePreviewStoredFile__IconFileNameContainer",
  componentId: "sc-850f8c32-3"
})([
  `padding:0 0.75rem;overflow:hidden;width:100%;display:flex;justify-content:center;`,
  `{padding:0 0.6875rem;}`
], containerPreviewCompact);
const ThumbnailImage = styled(Image).withConfig({
  displayName: "ResourcePreviewStoredFile__ThumbnailImage",
  componentId: "sc-850f8c32-4"
})([
  `height:auto;width:auto;max-width:100%;max-height:100%;object-fit:scale-down;display:block;border-radius:var(--preview-content-border-radius,1rem);border:var(--preview-border-width,2px) solid var(--preview-border-color,`,
  `);`,
  ` `,
  `{width:100%;height:100%;object-fit:cover;border:none;border-radius:0;`,
  `}`
], cssVar["color-border-secondary"], (p) => p.isSvg && css([
  `min-width:100px;min-height:100px;object-fit:contain;padding:1rem;`
]), containerPreviewCompact, (p) => p.isSvg && css([
  `object-fit:contain;`
]));
const Wrapper = styled.div.withConfig({
  displayName: "ResourcePreviewStoredFile__Wrapper",
  componentId: "sc-850f8c32-5"
})([
  `width:100%;height:100%;display:flex;flex-direction:column;gap:1rem;flex-grow:1;justify-content:center;align-items:center;position:relative;`,
  ` `,
  `{svg[data-file-icon]{height:60px !important;width:90px !important;}}&[data-preview-content-wrapper='stored_file_icon']{border-radius:1rem;}&[data-preview-content-wrapper='stored_document']{padding:1rem;padding-bottom:0;justify-content:unset;align-items:unset;overflow:hidden;background:`,
  `;border:var(--preview-border-width,2px) solid var(--preview-border-color,`,
  `);border-radius:var(--preview-content-border-radius,1.125rem);`,
  `{border:none;border-radius:0;padding:0.75rem;padding-bottom:0;gap:0.625rem;}&:after{position:absolute;content:'';display:block;bottom:0;left:0;right:0;height:2rem;background:linear-gradient( 180deg,rgba(`,
  `,0) 0%,rgba(`,
  `,1) 80% );}}`
], (p) => p.isAutoHeight ? css([
  `width:100%;`
]) : cssAspectRatio, containerPreviewCompact, cssVar["color-bg-primary"], cssVar["color-border-secondary"], containerPreviewCompact, cssVar["color-bg-primary-rgb"], cssVar["color-bg-primary-rgb"]);
const ThumbnailWrapper = styled(Flex).attrs({
  justifyContent: "center",
  alignItems: "center"
}).withConfig({
  displayName: "ResourcePreviewStoredFile__ThumbnailWrapper",
  componentId: "sc-850f8c32-6"
})([
  `position:relative;max-height:100%;max-width:100%;`,
  `{width:100%;height:100%;}`
], containerPreviewCompact);

export { ResourcePreviewFile };
//# sourceMappingURL=ResourcePreviewStoredFile.js.map
